import React, {useEffect, useRef, useState} from "react";
import Th_title from "../Th_title";
import UpdateInputBasic from "../input/UpdateInputBasic";
import {refresh, skeleton} from "../../redux/slice/apiSlice";
import {
    getAdminMenuList,
    getAdminMenuViewList,
    testAiConnect as testAiConnectApi,
    testSMTP as testSMTPApi,
    updateAdminMenu
} from "../../api/field/AdminMenuService";
import {useDispatch, useSelector} from "react-redux";
import {handleActiveToast} from "../sonner/ToastBasic";
import SimpleRadioGroup from "../input/SimpleRadioGroup";
import {
    AiManagementControl,
    DepartmentRadioOutput,
    InspectionReportOutput,
    ListManagementControl,
    SmtpAttributes
} from "../../constant/RadioGroupAttributes";
import {ButtonBasic} from "../buttons/ButtonBasic";
import {uploadLogo, uploadMenuLogo} from "../../api/file/FileService";
import PlatformLogo from "../../layouts/PlatformLogo";
import {isSystem} from "../commonmethod/CommonMethod";
import {useNavigate} from "react-router-dom";
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from "../../shadCn/components/ui/dialog";
import {AdminSettingsNavItem, AdminSettingsPanel, AdminSettingsShell} from "../admin/AdminSettingsLayout";
import "../admin/adminMenu.css";

function AdminMenu() {
    const status = useSelector(state => state.api.status);
    const user = useSelector(state => state.auth.user);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState({});
    const fileInputRef = useRef(null);
    const menuFileInputRef = useRef(null);
    const platFrom = useSelector(state => state.auth.platFrom);
    const navigate = useNavigate();
    const [aiConnectLoading, setAiConnectLoading] = useState(false);
    const [aiKeyModalOpen, setAiKeyModalOpen] = useState(false);
    const [aiKeyDraft, setAiKeyDraft] = useState("");
    const [activeNav, setActiveNav] = useState("branding");

    useEffect(() => {
        const fetchSettings = async () => {
            dispatch(skeleton(false));
            await getList().then(r => {
                if (r.status === 200) {
                    const menuList = r.data;
                    if (Array.isArray(menuList) && menuList.length > 0) {
                        const values = {};
                        menuList.forEach(item => {
                            values[item.globalsNmAsSnake] = {id: item.id, value: item.globalsValue};
                        });
                        setSettings(values);
                    }
                    setLoading(false);
                } else if (r.status === 201) {
                    if (r.data.errorCode === "AUTHORITY") {
                        alert(r.data.reason);
                        navigate(-1);
                    }
                }
            });
        };

        fetchSettings();
        dispatch(refresh(false));
    }, [status, dispatch, navigate]);

    function getList() {
        return getAdminMenuViewList();
    }

    const update = async (id, value) => {
        const response = await updateAdminMenu({id, value});
        if (response.status === 200) {
            const data = await getAdminMenuList();
            if (data.status === 200) {
                const filteredData = data.data.filter(each => each.storage === true);
                localStorage.setItem("setting", JSON.stringify(filteredData));
            }
            handleActiveToast({message: "데이터가 수정되었습니다"});
        }
    };

    const handleButtonClick = (kind) => {
        const ref = kind === "menu" ? menuFileInputRef : fileInputRef;
        ref.current?.click();
    };

    const handleImageUpload = async (e, kind) => {
        const file = e.target.files[0];
        if (file) {
            if (window.confirm("업로드하시겠습니까?")) {
                const response = kind === "menu"
                    ? await uploadMenuLogo(e.target.files)
                    : await uploadLogo(e.target.files);
                if (response === 200) {
                    alert("로고가 변경되었습니다.");
                    window.location.reload();
                }
            } else {
                const ref = kind === "menu" ? menuFileInputRef : fileInputRef;
                if (ref.current) {
                    ref.current.value = "";
                }
            }
        }
    };

    const testAiConnect = async () => {
        setAiConnectLoading(true);
        try {
            const response = await testAiConnectApi();
            if (response?.status === 200) {
                const message = typeof response.data === "string" ? response.data : "연결되었습니다.";
                alert(message);
            } else {
                alert("연결 실패");
            }
        } catch {
            alert("연결 실패");
        } finally {
            setAiConnectLoading(false);
        }
    };

    const handleSmtpTest = async () => {
        try {
            const response = await testSMTPApi();
            if (response?.status === 200) {
                alert("SMTP 테스트를 완료했습니다.");
            } else {
                alert("SMTP 테스트에 실패했습니다.");
            }
        } catch {
            alert("SMTP 테스트에 실패했습니다.");
        }
    };

    const handleOpenAiKeyModal = () => {
        setAiKeyDraft(settings.aiKeys?.value ?? "");
        setAiKeyModalOpen(true);
    };

    const handleSaveAiKey = async () => {
        if (!settings.aiKeys?.id) {
            alert("API 키 설정 항목을 찾을 수 없습니다.");
            return;
        }

        const response = await updateAdminMenu({id: settings.aiKeys.id, value: aiKeyDraft});
        if (response?.status === 200) {
            setSettings(prev => ({
                ...prev,
                aiKeys: {...prev.aiKeys, value: aiKeyDraft}
            }));
            handleActiveToast({message: "데이터가 수정되었습니다"});
            setAiKeyModalOpen(false);
        } else {
            handleActiveToast({message: "데이터수정이 실패하였습니다."});
        }
    };

    if (loading) {
        return (
            <div className="admin-settings-page common-container container-height-full border-l-gray">
                <div className="admin-settings-loading">설정을 불러오는 중…</div>
            </div>
        );
    }

    if (!settings || Object.keys(settings).length === 0) {
        return (
            <div className="admin-settings-page common-container container-height-full border-l-gray">
                <div className="admin-settings-loading">설정 정보를 불러올 수 없습니다.</div>
            </div>
        );
    }

    const navItems = [
        {id: "branding", label: "브랜딩"},
        {id: "mail", label: "메일 · SMTP"},
        {id: "policy", label: platFrom === "ITM" ? "보안 · 조직 · 보고서" : "보안 · 조직"},
        ...(isSystem(user) ? [{id: "system", label: "시스템 · AI"}] : []),
    ];

    const updateInput = (target, value, required = false) => (
        <UpdateInputBasic auth target={target} listener={updateAdminMenu} value={value} required={required}/>
    );

    const inputRow = (title, target, value, required = false) => (
        <tr>
            <Th_title title={title} colspan={1} className="fieldset-td fieldset-td-input" required={required} auth>
                {updateInput(target, value, required)}
            </Th_title>
        </tr>
    );

    const radioRow = (title, targetKey, attributes, required = false) => (
        <tr>
            <Th_title title={title} colspan={1} className="fieldset-td fieldset-td-input" required={required}>
                <div className="admin-settings-control">
                    <SimpleRadioGroup
                        radioAttributes={attributes}
                        defaultValue={settings[targetKey]?.value}
                        data={{data: settings, setData: setSettings}}
                        target={targetKey}
                        method={update}
                    />
                </div>
            </Th_title>
        </tr>
    );

    const actionRow = (title, description, button) => (
        <tr className="admin-settings-kv-row--auto">
            <Th_title title={title} colspan={1} className="fieldset-td">
                <div className="admin-settings-control admin-settings-control--action">
                    <span className="admin-settings-action-desc">{description}</span>
                    {button}
                </div>
            </Th_title>
        </tr>
    );

    return (
        <>
            <div className="admin-settings-page common-container container-height-full border-l-gray">
                <AdminSettingsShell
                    title="관리자 설정"
                    meta="운영관리 · 공통 · 관리자설정"
                    sidebar={navItems.map(item => (
                        <AdminSettingsNavItem
                            key={item.id}
                            targetId={item.id}
                            label={item.label}
                            active={activeNav === item.id}
                            onClick={setActiveNav}
                        />
                    ))}
                >
                    <div className="admin-settings-form">
                        {activeNav === "branding" && (
                            <AdminSettingsPanel
                                title="로고 이미지 변경"
                                description="로그인 화면과 메뉴 영역에 표시되는 플랫폼 로고를 관리합니다. 메뉴 로고를 등록하지 않으면 로고 이미지가 양쪽에 사용됩니다."
                            >
                                <tr className="admin-settings-kv-row--auto">
                                    <Th_title title="로고 이미지" colspan={1} className="fieldset-td fieldset-td-input" auth>
                                        <div className="admin-settings-control">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                style={{display: "none"}}
                                                ref={fileInputRef}
                                                onChange={(e) => handleImageUpload(e, "login")}
                                            />
                                            <div className="admin-settings-logo-box admin-settings-logo-box--dark">
                                                <PlatformLogo className=""/>
                                            </div>
                                            <ButtonBasic
                                                text="변경"
                                                className="w-[60px] h-[30px] bg-[#1c90fb] text-[white] justify-center line-height-30"
                                                variant="primary"
                                                onClick={() => handleButtonClick("login")}
                                            />
                                        </div>
                                    </Th_title>
                                </tr>
                                <tr className="admin-settings-kv-row--auto">
                                    <Th_title title="메뉴 로고 이미지" colspan={1} className="fieldset-td fieldset-td-input" auth>
                                        <div className="admin-settings-control">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                style={{display: "none"}}
                                                ref={menuFileInputRef}
                                                onChange={(e) => handleImageUpload(e, "menu")}
                                            />
                                            <div className="admin-settings-logo-box">
                                                <PlatformLogo variant="menu" className=""/>
                                            </div>
                                            <ButtonBasic
                                                text="변경"
                                                className="w-[60px] h-[30px] bg-[#1c90fb] text-[white] justify-center line-height-30"
                                                variant="primary"
                                                onClick={() => handleButtonClick("menu")}
                                            />
                                        </div>
                                    </Th_title>
                                </tr>
                            </AdminSettingsPanel>
                        )}

                        {activeNav === "mail" && (
                            <AdminSettingsPanel
                                title="SMTP 사용여부 및 설정"
                                description="시스템 알림 및 메일 발송에 사용되는 SMTP 서버 설정입니다."
                            >
                                {radioRow("SMTP 사용여부", "useSmtp", SmtpAttributes, true)}
                                {inputRow("Host", {id: settings.host?.id, fieldName: "globalsValue"}, settings.host?.value)}
                                {inputRow("Port", {id: settings.port?.id, fieldName: "globalsValue"}, settings.port?.value)}
                                {radioRow("Starttls.Enable", "starttlsEnable", SmtpAttributes, true)}
                                {radioRow("SSL", "sslEnable", SmtpAttributes, true)}
                                {radioRow("Auth", "auth", SmtpAttributes, true)}
                                {radioRow("Debug", "debug", SmtpAttributes, true)}
                                {inputRow("Username", {id: settings.username?.id, fieldName: "globalsValue"}, settings.username?.value)}
                                {inputRow("Password", {id: settings.password?.id, fieldName: "globalsValue"}, settings.password?.value)}
                                {inputRow("Charset", {id: settings.charset?.id, fieldName: "globalsValue"}, settings.charset?.value)}
                                {actionRow(
                                    "SMTP 테스트",
                                    "버튼 클릭 시 현재 접속자에게 테스트 메일을 발송합니다.",
                                    <button type="button" className="btn_save" onClick={handleSmtpTest}>
                                        테스트
                                    </button>
                                )}
                            </AdminSettingsPanel>
                        )}

                        {activeNav === "policy" && (
                            <>
                                <AdminSettingsPanel
                                    title="엑셀 업로드 비밀번호 세팅"
                                    description="엑셀 업로드 등 민감 기능에 사용되는 공통 비밀번호입니다."
                                >
                                    {inputRow(
                                        "사용자 비밀번호",
                                        {id: settings.excelUserPassword?.id, fieldName: "globalsValue"},
                                        settings.excelUserPassword?.value,
                                        true
                                    )}
                                </AdminSettingsPanel>

                                <AdminSettingsPanel
                                    title="동일 및 하위부서 보기설정"
                                    description="자산 목록에서 동일·하위 부서 데이터 조회 범위를 설정합니다."
                                >
                                    {radioRow("자산", "deptInvShow", DepartmentRadioOutput, true)}
                                </AdminSettingsPanel>

                                {platFrom === "ITM" && (
                                    <AdminSettingsPanel
                                        title="보고서 기준"
                                        description="점검 보고서 출력 기준을 설정합니다."
                                    >
                                        {radioRow("점검보고서 기준", "inspectionReport", InspectionReportOutput, true)}
                                    </AdminSettingsPanel>
                                )}
                            </>
                        )}

                        {activeNav === "system" && isSystem(user) && (
                            <>
                                <AdminSettingsPanel
                                    title="목록 구성 제어(시스템계정 기준)"
                                    description="목록 헤더 순서·필터 등 목록 구성 기능의 제어 기준을 설정합니다."
                                >
                                    {radioRow("제어 기준", "listManagementControl", ListManagementControl, true)}
                                </AdminSettingsPanel>

                                <AdminSettingsPanel
                                    title="AI 어시스턴트"
                                    description="AI 모델 연동 정보와 API 연결 설정을 관리합니다."
                                >
                                    {inputRow("모델", {id: settings.aiModel?.id, fieldName: "globalsValue"}, settings.aiModel?.value)}
                                    {inputRow("주소", {id: settings.aiUrl?.id, fieldName: "globalsValue"}, settings.aiUrl?.value)}
                                    <tr>
                                        <Th_title title="키" colspan={1} className="fieldset-td fieldset-td-input">
                                            <span className="edit-input cursor-pointer" onClick={handleOpenAiKeyModal}>
                                                {settings.aiKeys?.value ? "*******************" : "설정"}
                                            </span>
                                        </Th_title>
                                    </tr>
                                    {actionRow(
                                        "API 연결 테스트",
                                        "버튼 클릭 시 API 테스트를 실행합니다.",
                                        <button
                                            type="button"
                                            className="btn_save"
                                            onClick={testAiConnect}
                                            disabled={aiConnectLoading}
                                        >
                                            {aiConnectLoading ? "테스트 중…" : "테스트"}
                                        </button>
                                    )}
                                </AdminSettingsPanel>

                                <AdminSettingsPanel
                                    title="AI 어시스턴트 사용 제어"
                                    description="AI 분석·보고서 기능의 사용 범위를 설정합니다."
                                >
                                    {radioRow("제어 기준", "aiManagementControl", AiManagementControl, true)}
                                </AdminSettingsPanel>
                            </>
                        )}
                    </div>
                </AdminSettingsShell>
            </div>

            <Dialog open={aiKeyModalOpen} onOpenChange={setAiKeyModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>API 키 수정</DialogTitle>
                    </DialogHeader>
                    <div className="pt-2">
                        <input
                            type="password"
                            className="w-full border rounded px-3 py-2"
                            value={aiKeyDraft}
                            onChange={(e) => setAiKeyDraft(e.target.value)}
                            placeholder="API 키 입력"
                            autoComplete="off"
                        />
                    </div>
                    <DialogFooter>
                        <button type="button" className="btn_close" onClick={() => setAiKeyModalOpen(false)}>
                            취소
                        </button>
                        <button type="button" className="btn_save" onClick={handleSaveAiKey}>
                            저장
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

export {AdminMenu};
